import React from 'react';
import clsx from 'clsx';
import { BuildingStorefrontIcon, PaintBrushIcon } from '@heroicons/react/24/outline';
import { InspoBestSeller, InspoStory } from '../../../services';

// #region BrandMark
/** The brand's real logo when featured; a neutral icon tile when anonymous. Never an invented logo. */
export const BrandMark: React.FunctionComponent<{ story: InspoStory; size?: 'lg' | 'md' }> = ({ story, size = 'md' }) => {
    const box = size === 'lg' ? 'h-16 w-16' : 'h-11 w-11';
    if (story.logo_url) {
        return (
            <div className={clsx(box, 'shrink-0 overflow-hidden rounded-xl bg-white ring-1 ring-gray-200 dark:ring-stone-600')}>
                <img src={story.logo_url} alt={`${story.name} logo`} className="h-full w-full object-contain p-1" loading="lazy" />
            </div>
        );
    }
    const Icon = story.journey === 'artist' ? PaintBrushIcon : BuildingStorefrontIcon;
    return (
        <div className={clsx(box, 'flex shrink-0 items-center justify-center rounded-xl bg-seabreeze/40 text-blue-600 ring-1 ring-seabreeze dark:bg-blue-500/10 dark:text-blue-300 dark:ring-blue-500/20')}>
            <Icon className={size === 'lg' ? 'h-7 w-7' : 'h-5 w-5'} aria-hidden="true" />
        </div>
    );
};

// #region StatTiles
type Stat = { label: string; value: string; hint?: string };

/**
 * The supporting numbers: rounded when featured, ranges when anonymous (the server decides).
 * The hero already shows the monthly figure large, so it asks for the others only.
 */
export const statsOf = (story: InspoStory, withMonthly: boolean): Stat[] => {
    const m = story.figures.months_to_10k;
    // Only brands that started after our setup period have a meaningful "time to $10k".
    const pace: Stat = m === null
        ? { label: 'Best month', value: story.figures.best_month }
        : { label: 'To a $10k month', value: m === 0 ? 'Month 1' : `${m} months`, hint: 'from their first sale' };
    // A brand-new store has had no time for repeat customers yet; show its catalog instead of 0%.
    const loyalty: Stat = story.figures.repeat_rate > 0
        ? { label: 'Come back for more', value: `${story.figures.repeat_rate}%`, hint: 'of customers order again' }
        : { label: 'Designs', value: story.figures.designs };
    const monthly: Stat = { label: 'Customer sales a month', value: story.figures.monthly_now };
    return [...(withMonthly ? [monthly] : []), pace, { label: 'Customers', value: story.figures.customers }, loyalty];
};

export const StatTiles: React.FunctionComponent<{ story: InspoStory; withMonthly?: boolean }> = ({ story, withMonthly = false }) => (
    <dl className={clsx('grid gap-3', withMonthly ? 'grid-cols-2' : 'grid-cols-3')}>
        {statsOf(story, withMonthly).map((s) => (
            <div key={s.label} className="rounded-xl bg-gray-50 px-3 py-2.5 dark:bg-stone-800/70">
                <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{s.label}</dt>
                <dd className="mt-0.5 text-lg font-semibold tabular-nums text-gray-900 dark:text-gray-50">{s.value}</dd>
                {s.hint && <dd className="text-[11px] text-gray-400 dark:text-stone-500">{s.hint}</dd>}
            </div>
        ))}
    </dl>
);

// #region BestSellerTile
const BestSellerTile: React.FunctionComponent<{ item: InspoBestSeller; rank: number }> = ({ item, rank }) => {
    const src = item.mockup_thumb_url ?? item.image_url;
    return (
        <figure className="group min-w-0">
            <div className="relative aspect-[4/3] overflow-hidden rounded-lg bg-gray-100 ring-1 ring-gray-200 dark:bg-stone-800 dark:ring-stone-700">
                {src && <img src={src} alt={item.title} loading="lazy" className="h-full w-full object-cover transition duration-300 group-hover:scale-105" />}
                <span className="absolute left-1.5 top-1.5 rounded-md bg-white/90 px-1.5 py-0.5 text-[10px] font-semibold text-gray-800 shadow-sm dark:bg-stone-900/90 dark:text-gray-100">#{rank}</span>
            </div>
            <figcaption className="mt-1.5 line-clamp-2 text-xs text-gray-600 dark:text-gray-300">{item.title}</figcaption>
        </figure>
    );
};

// #region BestSellers
/** Up to six best sellers that have a picture, each keeping its true sales rank. */
export const BestSellers: React.FunctionComponent<{ items: InspoBestSeller[] }> = ({ items }) => {
    const shown = items
        .map((item, i) => ({ item, rank: i + 1 }))
        .filter(({ item }) => item.mockup_thumb_url || item.image_url)
        .slice(0, 6);
    if (!shown.length) return null;
    return (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {shown.map(({ item, rank }) => <BestSellerTile key={item.design_id} item={item} rank={rank} />)}
        </div>
    );
};
